import React, { use, useEffect, useState } from "react";
import { CompanyCashFlow } from "../../company";
import { useOutletContext } from "react-router-dom";
import { getCashflowStatement } from "../../api";
import Table from "../Table/Table";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  formatLargeMonetaryNumber,
  formatLargeNonMonetaryNumber,
  formatRatio,
} from "../Helpers/NumberFormatting";

interface Props {}

const configs = [
  // ── General Info ────────────────────────────
  { label: "Date", render: (c: CompanyCashFlow) => c.date },
  { label: "Fiscal Year", render: (c: CompanyCashFlow) => c.fiscalYear },
  { label: "Period", render: (c: CompanyCashFlow) => c.period },
  { label: "Currency", render: (c: CompanyCashFlow) => c.reportedCurrency },

  // ── Operating Activities ────────────────────
  {
    label: "Net Income",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.netIncome || 0)),
  },
  {
    label: "Depreciation & Amortization",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.depreciationAndAmortization || 0)),
  },
  {
    label: "Deferred Income Tax",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.deferredIncomeTax || 0)),
  },
  {
    label: "Stock-Based Compensation",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.stockBasedCompensation || 0)),
  },
  {
    label: "Change in Working Capital",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.changeInWorkingCapital || 0)),
  },
  {
    label: "Accounts Receivables",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.accountsReceivables || 0)),
  },
  {
    label: "Inventory",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.inventory || 0)),
  },
  {
    label: "Accounts Payables",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.accountsPayables || 0)),
  },
  {
    label: "Other Working Capital",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.otherWorkingCapital || 0)),
  },
  {
    label: "Other Non-Cash Items",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.otherNonCashItems || 0)),
  },
  {
    label: "Net Cash from Operating Activities",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.netCashProvidedByOperatingActivities || 0)),
  },

  // ── Investing Activities ────────────────────
  {
    label: "Investments in PPE",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.investmentsInPropertyPlantAndEquipment || 0)),
  },
  {
    label: "Acquisitions (Net)",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.acquisitionsNet || 0)),
  },
  {
    label: "Purchases of Investments",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.purchasesOfInvestments || 0)),
  },
  {
    label: "Sales/Maturities of Investments",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.salesMaturitiesOfInvestments || 0)),
  },
  {
    label: "Other Investing Activities",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.otherInvestingActivities || 0)),
  },
  {
    label: "Net Cash from Investing Activities",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.netCashProvidedByInvestingActivities || 0)),
  },

  // ── Financing Activities ────────────────────
  {
    label: "Net Debt Issuance",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.netDebtIssuance || 0)),
  },
  {
    label: "Long-Term Debt Issuance",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.longTermNetDebtIssuance || 0)),
  },
  {
    label: "Short-Term Debt Issuance",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.shortTermNetDebtIssuance || 0)),
  },
  {
    label: "Net Stock Issuance",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.netStockIssuance || 0)),
  },
  {
    label: "Common Stock Issuance",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.commonStockIssuance || 0)),
  },
  {
    label: "Common Stock Repurchased",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.commonStockRepurchased || 0)),
  },
  {
    label: "Net Dividends Paid",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.netDividendsPaid || 0)),
  },
  {
    label: "Other Financing Activities",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.otherFinancingActivities || 0)),
  },
  {
    label: "Net Cash from Financing Activities",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.netCashProvidedByFinancingActivities || 0)),
  },

  // ── Summary ─────────────────────────────────
  {
    label: "Effect of Forex on Cash",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.effectOfForexChangesOnCash || 0)),
  },
  {
    label: "Net Change in Cash",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.netChangeInCash || 0)),
  },
  {
    label: "Cash at Beginning",
    render: (c: CompanyCashFlow) =>
      formatLargeMonetaryNumber(Number(c.cashAtBeginningOfPeriod || 0)),
  },
  {
    label: "Cash at End",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.cashAtEndOfPeriod || 0)),
  },

  // ── Derived Metrics ─────────────────────────
  {
    label: "Operating Cash Flow",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.operatingCashFlow || 0)),
  },
  {
    label: "Capital Expenditure",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.capitalExpenditure || 0)),
  },
  {
    label: "Free Cash Flow",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.freeCashFlow || 0)),
  },
  {
    label: "Income Taxes Paid",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.incomeTaxesPaid || 0)),
  },
  {
    label: "Interest Paid",
    render: (c: CompanyCashFlow) => formatLargeMonetaryNumber(Number(c.interestPaid || 0)),
  },
];

const CashflowStatement = (props: Props) => {
  const ticker = useOutletContext<string>();
  const [cashflowData, setCashflowData] = useState<CompanyCashFlow>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getCashflowStatement(ticker!);
        setCashflowData(response!.data);
      } catch (err: any) {
        setError(err.message || "Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 font-medium">⚠️ Error fetching data: {error}</div>;
  }

  return (
    <>
      {cashflowData ? (
        <Table data={cashflowData} config={configs} />
      ) : (
        <div className="text-gray-500">No data available.</div>
      )}
    </>
  );
};

export default CashflowStatement;
